import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const RACK_PRODUCT = 16217725665583;
const STRAP_VARIANT = 58688838992175;
const MARKER = '_cr_snowboard_included';
const CODE = 'CRSNOW-INCLUDED-V03-6F2C91';
let queue = Promise.resolve();

function configuration() {
  return document.querySelector('cr-snowboard-included[data-enabled="true"]');
}

function isGift(item) {
  return item.variant_id === STRAP_VARIANT && item.properties?.[MARKER] === 'v2';
}

async function request(path, payload) {
  const response = await fetch(`${window.Shopify.routes.root}${path}`, {
    method: payload ? 'POST' : 'GET',
    headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
    ...(payload ? { body: JSON.stringify(payload) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.status || data.errors) throw new Error(configuration()?.dataset.error);
  return data;
}

function sectionParameters() {
  return {
    sections: [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5),
    sections_url: window.location.pathname,
  };
}

async function synchronize(initial) {
  const config = configuration();
  if (!config) return initial;
  let cart = await request('cart.js');
  const legacy = cart.items.filter((item) => item.variant_id === 51039627378991 && item.properties?.[MARKER] === 'v1');
  if (legacy.length) cart = await request('cart/update.js', { updates: Object.fromEntries(legacy.map((item) => [item.key, 0])) });
  const required = cart.items.reduce((total, item) => total + (item.product_id === RACK_PRODUCT ? item.quantity : 0), 0);
  let gifts = cart.items.filter(isGift);
  const codes = (cart.discount_codes || []).map((entry) => entry.code);
  const hasCode = codes.some((code) => code.toUpperCase() === CODE);
  if (!required && !gifts.length && !hasCode) return initial || cart;
  if (gifts.length === 1 && gifts[0].quantity === required && gifts[0].final_line_price === 0 && hasCode) {
    return initial || cart;
  }

  const updates = {};
  for (const [index, gift] of gifts.entries()) updates[gift.key] = index === 0 ? required : 0;
  if (Object.keys(updates).length) cart = await request('cart/update.js', { updates });
  const keptCodes = codes.filter((code) => code.toUpperCase() !== CODE);
  cart = await request('cart/update.js', { discount: keptCodes.join(','), ...sectionParameters() });

  if (required && !gifts.length) {
    await request('cart/add.js', { items: [{ id: STRAP_VARIANT, quantity: required, properties: { [MARKER]: 'v2' } }] });
  }
  if (required) {
    keptCodes.unshift(CODE);
    cart = await request('cart/update.js', { discount: keptCodes.join(','), ...sectionParameters() });
  }
  gifts = cart.items.filter(isGift);
  if (required && (gifts.reduce((sum, item) => sum + item.quantity, 0) !== required || gifts.some((item) => item.final_line_price !== 0))) {
    const rollback = Object.fromEntries(gifts.map((item) => [item.key, 0]));
    if (gifts.length) await request('cart/update.js', { updates: rollback });
    throw new Error(config.dataset.error);
  }
  return cart;
}

export function syncSnowboardStraps(cart) {
  if (!configuration()) return Promise.resolve(cart);
  const next = queue.catch(() => {}).then(() => synchronize(cart));
  queue = next;
  return next;
}

/** @extends Component<{status: HTMLElement}> */
class CrSnowboardIncluded extends Component {
  #controller;
  #ready = false;
  #checkout = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.enabled !== 'true') return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.documentElement.dataset.crSnowIncluded = 'true';
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, { signal });
    document.addEventListener('submit', this.onCheckout, { capture: true, signal });
    document.addEventListener('click', this.onCheckoutLink, { capture: true, signal });
    this.refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  async refresh() {
    this.#ready = false;
    this.refs.status.textContent = this.dataset.updating;
    try {
      const cart = await syncSnowboardStraps();
      if (!this.isConnected) return;
      this.refs.status.textContent = '';
      this.#ready = true;
      document.documentElement.dataset.crSnowRackInCart = cart.items.some((item) => item.product_id === RACK_PRODUCT) ? 'true' : 'false';
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines: [], promise: deferred.promise,
      }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: cart.sections, source: 'cr-snowboard-included', didError: false },
      });
    } catch {
      if (this.isConnected) this.refs.status.textContent = this.dataset.error;
    }
  }

  /** @param {CartLinesUpdateEvent} event */
  onCartUpdate = (event) => {
    if (event.target === this) return;
    this.#ready = false;
    event.promise?.then(() => this.refresh(), () => this.refresh());
  };

  /** @param {SubmitEvent} event */
  onCheckout = async (event) => {
    const button = event.submitter;
    if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement) || button.name !== 'checkout') return;
    if (this.#checkout && this.#ready) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    button.disabled = true;
    await this.refresh();
    button.disabled = false;
    if (!this.#ready) return;
    this.#checkout = true;
    form.requestSubmit(button);
    this.#checkout = false;
  };

  /** @param {MouseEvent} event */
  onCheckoutLink = async (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !/\/checkouts?(?:\/|\?|$)/.test(new URL(link.href).pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    await this.refresh();
    if (this.#ready) window.location.assign(link.href);
  };
}

if (!customElements.get('cr-snowboard-included')) customElements.define('cr-snowboard-included', CrSnowboardIncluded);
